import React from 'react';

import { formatSeparateByThousands, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

/**
 * Recruitment overview: what is late, what is about to start, and where the
 * hiring pinch points are.
 *
 * Money keys (`saving`, `rate`, `value`) are OMITTED — not nulled — for a user
 * without the pay-rate permission, so every one of them is guarded with
 * `key in obj` and simply not rendered. Nothing falls back to a zero.
 */

const HORIZON_LABELS = {
    next_30_days: () => $t('Next 30 days'),
    next_60_days: () => $t('Next 60 days'),
    next_90_days: () => $t('Next 90 days'),
    beyond_90_days: () => $t('Beyond 90 days'),
    overdue: () => $t('Overdue'),
};

/** Unknown horizon keys degrade to a readable label rather than disappearing. */
function horizonLabel(horizon) {
    const known = HORIZON_LABELS[horizon];

    if (known) {
        return known();
    }

    return String(horizon || '')
        .replace(/_/g, ' ')
        .replace(/^\w/, (character) => character.toUpperCase());
}

function Panel({ title, children }) {
    return (
        <div>
            <h4 className="tw-text-sm tw-font-semibold tw-text-charcoal tw-mb-3">{title}</h4>
            {children}
        </div>
    );
}

function Empty({ children }) {
    return <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">{children}</p>;
}

/** Late by N weeks. Red is the alert colour here, which is exactly what it means. */
function WeeksLateTag({ weeks }) {
    if (!weeks) {
        return null;
    }

    return (
        <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-red-10 tw-text-2xs tw-font-semibold tw-text-red tw-whitespace-nowrap">
            {weeks === 1 ? $t('1 week late') : $t('{{count}} weeks late', { count: weeks })}
        </span>
    );
}

function BehindSchedule({ positions }) {
    if (!positions.length) {
        return <Empty>{$t('Nothing is behind schedule.')}</Empty>;
    }

    return (
        <ul className="tw-list-none tw-p-0 tw-m-0 tw-divide-y tw-divide-grey">
            {positions.map((position) => (
                <li key={position.position_id} className="tw-flex tw-items-center tw-justify-between tw-gap-x-3 tw-py-2">
                    <div className="tw-min-w-0">
                        <div className="tw-text-sm tw-text-charcoal tw-truncate">{position.name}</div>
                        {!!position.level && <div className="tw-text-xs tw-text-charcoal-50">{position.level}</div>}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-x-3 tw-shrink-0">
                        <WeeksLateTag weeks={position.weeks_late} />
                        {'saving' in position && (
                            <span className="tw-text-sm tw-font-semibold tw-text-green tw-whitespace-nowrap">
                                {$t('{{amount}} saved', { amount: formatToFixedMoneyWithLocale(position.saving) })}
                            </span>
                        )}
                    </div>
                </li>
            ))}
        </ul>
    );
}

/** Starts by horizon carry a value, not just a count — the count alone hides the cost of a hiring wave. */
function StartsByHorizon({ horizons }) {
    if (!horizons.length) {
        return <Empty>{$t('No starts are scheduled.')}</Empty>;
    }

    return (
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3">
            {horizons.map((horizon) => (
                <div key={horizon.horizon} className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-charcoal-10">
                    <div className="tw-text-xs tw-font-medium tw-text-charcoal-50">{horizonLabel(horizon.horizon)}</div>
                    <div className="tw-text-xl tw-font-semibold tw-text-charcoal">{formatSeparateByThousands(horizon.count)}</div>
                    {'value' in horizon && <div className="tw-text-xs tw-text-charcoal-75">{formatToFixedMoneyWithLocale(horizon.value)}</div>}
                </div>
            ))}
        </div>
    );
}

function NextToStart({ positions }) {
    if (!positions.length) {
        return <Empty>{$t('No upcoming starts.')}</Empty>;
    }

    return (
        <ul className="tw-list-none tw-p-0 tw-m-0 tw-divide-y tw-divide-grey">
            {positions.map((position) => (
                <li key={position.position_id} className="tw-flex tw-items-center tw-justify-between tw-gap-x-3 tw-py-2">
                    <div className="tw-min-w-0">
                        <div className="tw-text-sm tw-text-charcoal tw-truncate">{position.name}</div>
                        {!!position.level && <div className="tw-text-xs tw-text-charcoal-50">{position.level}</div>}
                    </div>
                    <span className="tw-text-sm tw-text-charcoal-75 tw-shrink-0">{position.start_date}</span>
                </li>
            ))}
        </ul>
    );
}

/**
 * Pinch quarters — where starts bunch up. Each is flagged with the child level
 * that owns most of them, so the reader knows who to talk to.
 */
function PinchQuarters({ quarters }) {
    if (!quarters.length) {
        return <Empty>{$t('No hiring pinch points.')}</Empty>;
    }

    return (
        <ul className="tw-list-none tw-p-0 tw-m-0 tw-divide-y tw-divide-grey">
            {quarters.map((quarter) => (
                <li key={quarter.label} className="tw-flex tw-items-center tw-justify-between tw-gap-x-3 tw-py-2">
                    <div className="tw-min-w-0">
                        <div className="tw-text-sm tw-text-charcoal">{quarter.label}</div>
                        {!!quarter.owning_child?.label && <div className="tw-text-xs tw-text-charcoal-50 tw-truncate">{quarter.owning_child.label}</div>}
                    </div>
                    <span className="tw-text-sm tw-font-semibold tw-text-charcoal tw-shrink-0">
                        {$t('{{count}} starts', { count: formatSeparateByThousands(quarter.starts) })}
                    </span>
                </li>
            ))}
        </ul>
    );
}

export default function PeopleRecruitmentOverview({ recruitment }) {
    if (!recruitment) {
        return null;
    }

    return (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-6">
            <Panel title={$t('Behind schedule')}>
                <BehindSchedule positions={recruitment.behind_schedule || []} />
            </Panel>
            <Panel title={$t('Next to start')}>
                <NextToStart positions={recruitment.next_to_start || []} />
            </Panel>
            <Panel title={$t('Starts by horizon')}>
                <StartsByHorizon horizons={recruitment.starts_by_horizon || []} />
            </Panel>
            <Panel title={$t('Pinch quarters')}>
                <PinchQuarters quarters={recruitment.pinch_quarters || []} />
            </Panel>
        </div>
    );
}
